import * as React from 'react';
import * as PXBCore from '@brightlayer-ui/react-components/core';
import * as PXBColors from '@brightlayer-ui/colors';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import { getIcon } from '../../lib/icons';
import { Typography, IconButton, ListItemText, CircularProgress, Avatar } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { pushAppRoute } from '../../data/domain/route-manager';
import { AppRoute } from '../../routes';
import { useHistory } from 'react-router-dom';
import { History } from 'history';
import * as MuiIcons from '@mui/icons-material';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useAlarmStatusData } from '../../data/DataProviders/AlarmStatusDataProvider';
import { isDataLoading, isDataError, isDataLoaded } from '../../types';
import { TimelineCardEvent, Props } from './types';
import { getPrecisionValue } from '../../lib/precision-helper';
import { AlarmStatus } from '../../lib/alarm-helpers';
import { AlarmBadge } from '../AlarmBadge/AlarmBadge';

const useStyles = makeStyles({
    wrapText: {
        lineHeight: 1.25,
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    wrapTitle: {
        lineHeight: 1.25,
        textOverflow: 'ellipsis',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',

        display: '-webkit-box',
    },
});

const DateTime: React.FC<{ dateTime: string }> = (props) => {
    const languageLocale = useLanguageLocale();

    return (
        <div style={{ marginRight: '10px' }}>
            <Typography variant="caption" display="block" style={{ whiteSpace: 'pre' }}>
                {languageLocale.formatTimeShort(props.dateTime)}
            </Typography>
            <Typography variant="caption" display="block">
                {languageLocale.formatDateShort(props.dateTime)}
            </Typography>
        </div>
    );
};

const navigateToEventDetail = (eventData: TimelineCardEvent, history: History<any>, appRoute: AppRoute): void => {
    if ('alarmId' in appRoute) {
        pushAppRoute(history, {
            ...appRoute,
            alarmId: eventData.alarmId,
            deviceId: eventData.deviceId,
        });
    }
};

export const TimelineListItem: React.FC<Props> = (props) => {
    const history = useHistory();
    const classes = useStyles();
    const alarmStatusData = useAlarmStatusData();
    const [alarmStatus, setAlarmStatus] = React.useState('');
    React.useEffect(() => {
        if (props.value.status !== undefined) setAlarmStatus(props.value.status);
        else if (isDataLoaded(alarmStatusData)) setAlarmStatus(alarmStatusData.value.status);
    }, [alarmStatusData, props.value.status]);

    const getAlarmStatus = (status: string): JSX.Element => {
        switch (status) {
            case AlarmStatus.Open: {
                // red with circle
                return (
                    <Avatar style={{ backgroundColor: PXBColors.red[500] }}>
                        <NotificationsActive style={{ color: PXBColors.white[500] }} />
                    </Avatar>
                );
            }
            case AlarmStatus.Acknowledged: {
                return (
                    <Avatar style={{ backgroundColor: PXBColors.red[500] }}>
                        <NotificationsActive style={{ color: PXBColors.white[500] }} />
                    </Avatar>
                );
            }
            case AlarmStatus.Cleared:
                return React.createElement(getIcon('Notifications'), { style: { fill: PXBColors.red[500] } });
            case AlarmStatus.Closed:
                return React.createElement(getIcon('Notifications'), { style: { fill: PXBColors.black[200] } });
            default:
                return React.createElement(getIcon('NotInterested'), { style: { fill: PXBColors.black[200] } });
        }
    };
    const getAlarmIconOnLoad = (): JSX.Element => {
        if (isDataLoading(alarmStatusData)) {
            return <CircularProgress id="progress-spinner" size="24px" />;
        }

        if (isDataError(alarmStatusData)) {
            return React.createElement(getIcon('ErrorOutline'));
        }
        return getAlarmStatus(alarmStatusData.value.status);
    };
    const getAlarmIcon = (): JSX.Element => {
        if (props.value.status !== undefined) return getAlarmStatus(props.value.status);

        return getAlarmIconOnLoad();
    };

    const yelling =
        isDataLoaded(alarmStatusData) &&
        (alarmStatus === AlarmStatus.Open ||
            alarmStatus === AlarmStatus.Acknowledged ||
            alarmStatus === AlarmStatus.Cleared);

    return (
        <div data-testid={props.testId}>
            <PXBCore.InfoListItem
                avatar={false}
                statusColor={yelling ? PXBColors.red[500] : PXBColors.white[50]}
                title=""
                backgroundColor={PXBColors.white[50]}
                divider={!props.isLast ? 'full' : undefined}
                icon={getAlarmIcon()}
                onClick={(): void => {
                    if (props.appRoute) {
                        navigateToEventDetail(props.value, history, props.appRoute);
                    }
                }}
                leftComponent={
                    <>
                        <div>
                            <DateTime dateTime={props.value.dateTime} />
                        </div>
                        <ListItemText data-testid="alarmDescription">
                            <Typography
                                className={classes.wrapTitle}
                                variant={props.viewType === 'small' ? 'body2' : 'body1'}
                                display="inline"
                                style={{
                                    lineHeight: 1.25,
                                    fontWeight: props.viewType === 'small' ? 600 : 'normal',
                                }}
                            >
                                {props.options &&
                                    props.options.showDeviceName &&
                                    `${props.value.trigger || ''} ${props.value.severity || ''} ${
                                        props.value.channelName
                                    } ${props.value.deviceName} ${getPrecisionValue(props.value.value)} ${
                                        props.value.unit
                                    }`}
                            </Typography>
                            <Typography data-testid="path" variant="body2" display="block" className={classes.wrapText}>
                                {props.options && props.options.showPath && [...props.value.path].reverse().join(' < ')}
                            </Typography>
                        </ListItemText>
                    </>
                }
                rightComponent={
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {props.options && props.options.showBadges && isDataLoaded(alarmStatusData) && (
                            <AlarmBadge alarmStatus={alarmStatus} />
                        )}
                        <IconButton size="large">
                            <MuiIcons.ChevronRight />
                        </IconButton>
                    </div>
                }
            />
        </div>
    );
};
